import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import routes from '../../../routesConfig';
import {refreshInquirySummary, useInquirySummary} from '../../../utils/inquirySummary';
import {useAuth} from '../../../contexts/AuthContext';
import {
    deviceLabel,
    formatAge,
    formatTerm,
    inquirySummary,
    relativeDay,
    reservationDevices,
    STATUS_FILTERS,
    StatusBadge,
    TYPE_OPTIONS,
    TypeBadge,
    userName
} from './inquiryUtils';

/** Nový požadavek bez reakce déle než tato doba se zvýrazní. */
const OVERDUE_HOURS = 2;

const isOverdue = (row) => row.status === 'new' && Date.now() - new Date(row.createdAt).getTime() > OVERDUE_HOURS * 3600 * 1000;

const waitingFor = (row) => {
    const hours = Math.floor((Date.now() - new Date(row.createdAt).getTime()) / 3600000);
    return hours < 24 ? `čeká ${hours} h` : `čeká ${Math.floor(hours / 24)} d`;
};

const REFRESH_MS = 60 * 1000;

/** Přehled požadavků z webu – jedna schránka pro rezervace, zprávy a poptávky. */
const Inquiries = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const type = params.get('typ') || '';
    const statusFilter = params.get('stav') || 'open';
    const assigneeFilter = params.get('resi') || '';
    const {user} = useAuth();
    const [query, setQuery] = useState('');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const summary = useInquirySummary();

    const load = useCallback(async () => {
        try {
            const {data} = await API.get(API_ENDPOINTS.webInquiry.list, {meta: {silentError: true}});
            setRows(data || []);
            setError(null);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        refreshInquirySummary();
        const timer = setInterval(load, REFRESH_MS);
        return () => clearInterval(timer);
    }, [load]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value);
        else next.delete(key);
        setParams(next, {replace: true});
    };

    const statuses = STATUS_FILTERS.find((item) => item.value === statusFilter)?.statuses || null;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = rows.filter((row) => {
            if (type && row.type !== type) return false;
            if (statuses && !statuses.includes(row.status)) return false;
            if (assigneeFilter === 'ja' && String(row.assignedTo?._id || '') !== String(user?._id)) return false;
            if (assigneeFilter === 'nikdo' && row.assignedTo) return false;
            if (!q) return true;
            const haystack = [
                row.number,
                row.contact?.name,
                row.contact?.email,
                row.contact?.phone,
                String(row.contact?.phone || '').replace(/\s/g, ''),
                row.contact?.company,
                inquirySummary(row)
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(q);
        });
        // Rezervace k vyřízení řadit podle termínu, ostatní od nejnovějších
        if (type === 'reservation' && statusFilter === 'open') {
            return [...list].sort((a, b) =>
                `${a.reservation?.date} ${a.reservation?.time}`.localeCompare(`${b.reservation?.date} ${b.reservation?.time}`));
        }
        return list;
    }, [rows, type, statuses, statusFilter, query, assigneeFilter, user?._id]);

    const overdueCount = rows.filter(isOverdue).length;

    const countFor = (typeValue) => {
        const openStatuses = STATUS_FILTERS[0].statuses;
        return rows.filter((row) => (!typeValue || row.type === typeValue) && openStatuses.includes(row.status)).length;
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl font-semibold">Požadavky z webu</h1>
                    <p className="text-sm text-gray-600">
                        Rezervace, zprávy a poptávky odeslané z applelab.cz.
                        {summary?.newTotal ? <> Nových: <strong>{summary.newTotal}</strong>.</> : null}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Link to={routes.admin.inquiries.calendarFull} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50">
                        Kalendář rezervací
                    </Link>
                </div>
            </div>

            {summary?.today?.length ? (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm">
                    <strong>Dnes přijde {summary.today.length} {summary.today.length === 1 ? 'zákazník' : summary.today.length < 5 ? 'zákazníci' : 'zákazníků'}:</strong>{' '}
                    {summary.today.map((item, index) => (
                        <span key={item._id}>
                            {index ? ', ' : ''}
                            <Link className="text-blue-700 hover:underline" to={routes.admin.getInquiry(item._id)}>
                                {item.reservation?.time} {item.contact?.name}
                            </Link>
                            {item.status === 'new' ? ' (nepotvrzeno)' : ''}
                        </span>
                    ))}
                </div>
            ) : null}

            <div className="flex flex-wrap gap-1 border-b border-gray-200" role="tablist">
                {[{value: '', short: 'Vše'}, ...TYPE_OPTIONS].map((item) => {
                    const active = type === item.value;
                    const count = countFor(item.value);
                    const newCount = item.value ? summary?.newByType?.[item.value] : summary?.newTotal;
                    return (
                        <button
                            key={item.value || 'all'}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setParam('typ', item.value)}
                            className={`-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm ${active ? 'border-indigo-600 font-semibold text-indigo-700' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
                        >
                            {item.short}
                            <span className="text-xs text-gray-500">{count}</span>
                            {newCount ? <span className="rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">{newCount}</span> : null}
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                {STATUS_FILTERS.map((item) => (
                    <button
                        key={item.value}
                        type="button"
                        onClick={() => setParam('stav', item.value === 'open' ? '' : item.value)}
                        className={`rounded-full border px-3 py-1 text-sm ${statusFilter === item.value ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
                    >
                        {item.label}
                    </button>
                ))}
                <select
                    value={assigneeFilter}
                    onChange={(event) => setParam('resi', event.target.value)}
                    className="rounded-full border border-gray-300 bg-white px-3 py-1 text-sm"
                    aria-label="Kdo řeší"
                >
                    <option value="">Řeší: kdokoli</option>
                    <option value="ja">Řeším já</option>
                    <option value="nikdo">Nikdo neřeší</option>
                </select>
                <input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Hledat jméno, telefon, e-mail, číslo…"
                    className="ml-auto w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm sm:w-72"
                />
            </div>

            {overdueCount ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <strong>{overdueCount}</strong> {overdueCount === 1 ? 'nový požadavek čeká' : 'nové požadavky čekají'} na reakci déle než {OVERDUE_HOURS} hodiny.
                </div>
            ) : null}

            {error ? <div className="text-sm text-red-600">Požadavky se nepodařilo načíst. Zkuste obnovit stránku.</div> : null}

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                {loading ? (
                    <div className="p-6 text-center text-sm text-gray-500">Načítám…</div>
                ) : filtered.length ? (
                    <ul className="divide-y divide-gray-100">
                        {filtered.map((row) => (
                            <InquiryRow key={row._id} row={row} onOpen={() => navigate(routes.admin.getInquiry(row._id))}/>
                        ))}
                    </ul>
                ) : (
                    <div className="p-6 text-center text-sm text-gray-500">
                        {statusFilter === 'open' ? 'Vše vyřízeno – žádné požadavky nečekají.' : 'Žádné požadavky neodpovídají filtru.'}
                    </div>
                )}
            </div>
        </div>
    );
};

const InquiryRow = ({row, onOpen}) => {
    const unread = !row.readAt;
    const reservation = row.reservation || {};
    const linked = row.links?.repair || row.links?.buyback;
    const overdue = isOverdue(row);
    return (
        <li>
            <button
                type="button"
                onClick={onOpen}
                className={`grid w-full grid-cols-1 gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-gray-50 md:grid-cols-[1.25rem_7rem_minmax(0,1.3fr)_minmax(0,2fr)_9rem_8rem] md:items-center ${overdue ? 'border-l-4 border-l-red-500 bg-red-50/50' : unread ? 'bg-amber-50/40' : ''}`}
            >
                <span className="hidden md:block" aria-hidden="true">
                    {unread ? <span className="block h-2.5 w-2.5 rounded-full bg-blue-600" title="Nepřečteno"/> : null}
                </span>
                <span className="flex items-center gap-2 md:flex-col md:items-start md:gap-1">
                    <TypeBadge type={row.type}/>
                    <span className="text-xs text-gray-500">{row.number}</span>
                </span>
                <span className="min-w-0">
                    <span className={`block truncate ${unread ? 'font-semibold' : ''}`}>
                        {unread ? <span className="mr-1 text-blue-600 md:hidden">●</span> : null}
                        {row.contact?.company || row.contact?.name || '-'}
                    </span>
                    <span className="block truncate text-xs text-gray-500">
                        {[row.contact?.company ? row.contact?.name : null, row.contact?.phone || row.contact?.email].filter(Boolean).join(' · ')}
                    </span>
                </span>
                <span className="min-w-0">
                    <span className={`block truncate text-sm ${unread ? 'font-medium' : ''}`}>{inquirySummary(row) || '-'}</span>
                    {linked ? (
                        <span className="block truncate text-xs text-emerald-700">
                            {row.links?.repair ? `Zakázka ${row.links.repair.repairNumber || ''}` : `Výkup ${row.links.buyback.buybackNumber || ''}`}
                        </span>
                    ) : row.type === 'reservation' && !reservationDevices(reservation).some((item) => item.repairs?.length) && deviceLabel(row) ? (
                        <span className="block truncate text-xs text-gray-500">Bez vybrané opravy</span>
                    ) : null}
                </span>
                <span className="text-sm">
                    {row.type === 'reservation' ? (
                        <>
                            <span className="block font-medium">{formatTerm(reservation.date, reservation.time)}</span>
                            <span className="block text-xs text-gray-500">{relativeDay(reservation.date)}</span>
                        </>
                    ) : (
                        <span className="text-xs text-gray-500">{formatAge(row.createdAt)}</span>
                    )}
                </span>
                <span className="flex items-center justify-between gap-2 md:block">
                    <StatusBadge status={row.status} type={row.type}/>
                    {overdue ? (
                        <span className="block text-xs font-medium text-red-700 md:mt-1">{waitingFor(row)}</span>
                    ) : row.type === 'reservation' ? <span className="block text-xs text-gray-400 md:mt-1">{formatAge(row.createdAt)}</span> : null}
                    {row.assignedTo ? <span className="block truncate text-xs text-gray-600 md:mt-0.5" title="Řeší">👤 {userName(row.assignedTo)}</span> : null}
                </span>
            </button>
        </li>
    );
};

export default Inquiries;
